import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  UserActionsContext,
  UserContext,
  UserStoreContext,
  type UserState,
  type UserStore,
} from './user-context';
import { createExternalStore } from '@/shared/libs/create-external-store';
import Cookie from 'js-cookie';
import { formatPhoneNumber } from '@/shared/libs/format-phone-number';
import useSWR from 'swr';

import { fetcherWithToken } from '@/shared/api/fetcher';
import { setGtmUserId } from '@/shared/libs/analytics/gtm';

const AUTH_STATUS = {
  ANONYMOUS: 'anonymous',
  CHECKING: 'checking',
  AUTHENTICATED: 'authenticated',
  LOGGING_OUT: 'loggingOut',
};

const getAnonymousUserState = () => ({
  isAuth: false,
  authStatus: AUTH_STATUS.ANONYMOUS,
  isBonusDepositReplenish: false,
  isUserLoading: false,
});

const isUnauthorizedError = (error: any) => error?.status === 401;

const getInitialUserState = () => {
  return {
    isAuth: false,
    authStatus: AUTH_STATUS.CHECKING,
    isBonusDepositReplenish: false,
    userDepositBonus: null,
    isUserLoading: true,
    isEmptyBalance: true,
  };
};

interface UserProviderProps {
  children: React.ReactNode;
}

export const useAuth = () => {
  const token =
    typeof document !== 'undefined' ? Cookie.get('sessionId') : undefined;
  const {
    data: wishlistData,
    error: wishlistError,
    mutate: mutateWishlist,
  } = useSWR(token ? '/favorites' : null, fetcherWithToken);

  const {
    data: profileData,
    error: profileError,
    mutate: mutateProfile,
  } = useSWR(token ? '/profile/getProfileData' : null, fetcherWithToken);

  const [mergedData, setMergedData] = useState<Partial<UserState> | null>(null);

  useEffect(() => {
    if (!(profileData && wishlistData)) return;

    const combinedData = {
      ...profileData,
      wishlist: wishlistData,
      phone: formatPhoneNumber(profileData.phone),
    };

    setMergedData(combinedData);
  }, [profileData, wishlistData]);

  return {
    token,
    data: mergedData,
    error: profileError || wishlistError,
    mutateWishlist: mutateWishlist,
    mutateProfile: mutateProfile,
  };
};

const UserProvider = ({ children }: UserProviderProps) => {
  const [user, setUser] = useState<UserState>(getInitialUserState);
  const storeRef = useRef<UserStore | null>(null);
  if (!storeRef.current) {
    storeRef.current = createExternalStore(user);
  }

  // useLayoutEffect — иначе useIsAuth()/useUserId() и другие точечные селекторы
  // на кадр отставали бы от useUser() (тот обновляется синхронно в этом же рендере)
  useLayoutEffect(() => {
    storeRef.current!.setState(user);
  }, [user]);

  const { token, data, error, mutateProfile, mutateWishlist } = useAuth();

  useEffect(() => {
    if (Cookie.get('sessionId')) return;

    setUser(getAnonymousUserState());
  }, []);

  useEffect(() => {
    if (!token) {
      setUser(getAnonymousUserState());
      setGtmUserId(undefined);
    } else if (data) {
      setUser((prev) => ({
        ...prev,
        ...data,
        isBonusDepositReplenish: false,
        isAuth: true,
        authStatus: AUTH_STATUS.AUTHENTICATED,
        isUserLoading: false,
      }));
      setGtmUserId(data.id);
    } else if (error) {
      if (isUnauthorizedError(error)) {
        Cookie.remove('sessionId');
      }

      setUser(getAnonymousUserState());
      setGtmUserId(undefined);
    }
  }, [token, data, error]);

  const refreshUser = useCallback(async () => {
    if (token) {
      setUser((prev) => ({
        ...prev,
        authStatus: AUTH_STATUS.CHECKING,
        isUserLoading: true,
      }));
      try {
        await mutateWishlist();
        await mutateProfile();
        setUser((prev) => ({
          ...prev,
          authStatus: prev.isAuth
            ? AUTH_STATUS.AUTHENTICATED
            : AUTH_STATUS.ANONYMOUS,
          isUserLoading: false,
        }));
      } catch (error: any) {
        if (isUnauthorizedError(error)) {
          Cookie.remove('sessionId');
        }

        // Любая ошибка при рефетче (не только 401) переводит юзера в anonymous
        // на клиенте — куку не трогаем, если это не 401, но UI уже считает
        // сессию невалидной. Если backend временно недоступен, пользователя
        // разлогинит визуально, хотя сессия на сервере ещё жива.
        setUser(getAnonymousUserState());
      }
    }
  }, [token, mutateProfile, mutateWishlist]);

  const userValue = useMemo(
    () => ({
      user,
      setUser,
      refreshUser,
      mutateWishlist,
    }),
    [user, refreshUser, mutateWishlist]
  );

  const userActions = useMemo(
    () => ({
      setUser,
      refreshUser,
      mutateWishlist,
    }),
    [refreshUser, mutateWishlist]
  );

  return (
    <UserStoreContext.Provider value={storeRef.current}>
      <UserActionsContext.Provider value={userActions}>
        <UserContext.Provider value={userValue}>
          {children}
        </UserContext.Provider>
      </UserActionsContext.Provider>
    </UserStoreContext.Provider>
  );
};

export { UserProvider };
